/**
 * Making sense of a path somebody typed or pasted.
 *
 * A path arrives from an email, a terminal, or a protocol document, and each
 * of those mangles it differently: quoted, backslash-escaped, with a trailing
 * slash, or written with a `~` that only a shell expands. Getting this wrong
 * means the folder "does not exist" for a reason the reader cannot see, which
 * is the worst kind of wrong for a field whose whole purpose is to save them a
 * trip through a dialog.
 *
 * Kept apart from the field that uses it so the rules can be tested without a
 * browser — they are the part anyone would argue with.
 */

/** `~/studies` → `/Users/x/studies`, and the quoting a shell adds. */
export function cleanPath(raw: string, home: string | null): string {
  let path = raw.trim();
  // A path dragged into a terminal and copied back arrives quoted; one with
  // spaces arrives backslash-escaped.
  if (
    (path.startsWith('"') && path.endsWith('"')) ||
    (path.startsWith("'") && path.endsWith("'"))
  ) {
    path = path.slice(1, -1);
  }
  path = path.replace(/\\ /g, " ");
  // Only at the start: `/tmp/~backup` is a folder called `~backup`.
  if (home && (path === "~" || path.startsWith("~/"))) {
    path = home + path.slice(1);
  }
  return path.replace(/\/+$/, "") || path;
}

/** The home directory implied by an absolute path the app already knows. */
export function homeFrom(path: string | null | undefined): string | null {
  const match = /^(\/Users\/[^/]+|\/home\/[^/]+)/.exec(path ?? "");
  return match ? match[1] : null;
}
